import { ArrowUp, Check, Sparkles, Undo2, X } from "lucide-react";

import { Button } from "@/components/shadcn/button";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/shadcn/collapsible";
import { NoxusInput } from "./InputField";
import { NoxusTooltip } from "../UI/NoxusTooltip";
import { cn } from "@/lib/utils";

export const LoadingDots = () => (
  <div className="loading-dots">
    <div className="dot"></div>
    <div className="dot"></div>
    <div className="dot"></div>
  </div>
);

// The floating generate/accept/revert cluster and the collapsible prompt box
// shared by CodeFieldAI (Run Code) and BindableCodeField (Query Table) — one
// source so the two editors can't drift.

export function CodeGenerateControls({
  hasGenerated,
  boxVisible,
  disabled,
  onToggleBox,
  onAccept,
  onRevert,
}: {
  hasGenerated: boolean;
  boxVisible: boolean;
  disabled?: boolean;
  onToggleBox: () => void;
  onAccept: () => void;
  onRevert: () => void;
}) {
  return (
    <div className="absolute bottom-2 right-2 flex gap-2 z-10">
      {hasGenerated && (
        <NoxusTooltip content="Revert" aria-label="Revert" asChild>
          <Button
            onClick={onRevert}
            size="icon"
            variant="outline"
            className={cn(
              "w-7 h-7 rounded-full bg-white",
              "transition-transform duration-100 ease-in-out active:scale-90"
            )}
          >
            <Undo2 size={16} strokeWidth={1} />
          </Button>
        </NoxusTooltip>
      )}
      <NoxusTooltip
        content={
          hasGenerated ? "Accept" : boxVisible ? "Cancel" : "Generate code"
        }
        aria-label={
          hasGenerated ? "Accept" : boxVisible ? "Cancel" : "Generate code"
        }
        asChild
      >
        <Button
          onClick={() => {
            if (hasGenerated) {
              onAccept();
            } else {
              onToggleBox();
            }
          }}
          size="icon"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-7 h-7 rounded-full",
            "transition-transform duration-100 ease-in-out active:scale-90"
          )}
          tabIndex={-1}
        >
          {hasGenerated ? (
            <Check size={16} />
          ) : boxVisible ? (
            <X size={16} />
          ) : (
            <Sparkles size={16} strokeWidth={1} />
          )}
        </Button>
      </NoxusTooltip>
    </div>
  );
}

export function CodeGeneratePromptBox({
  visible,
  value,
  onChange,
  onSubmit,
  isLoading,
  placeholder,
}: {
  visible: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  placeholder: string;
}) {
  return (
    <Collapsible open={visible}>
      <CollapsibleContent>
        <div className="mt-2 gap-2 flex items-start overflow-visible">
          <NoxusInput
            type="text"
            placeholder={placeholder}
            aria-label={placeholder}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.shiftKey) return;
              e.preventDefault();
              onSubmit();
            }}
            isTextarea={true}
            isAutoResize={true}
          />
          <Button
            onClick={onSubmit}
            size="icon"
            className={cn(
              "rounded-lg transition-transform duration-100 ease-in-out w-8 h-8",
              "active:scale-90"
            )}
            disabled={!value || isLoading}
          >
            {isLoading ? (
              <LoadingDots />
            ) : (
              <ArrowUp size={24} strokeWidth={1.5} />
            )}
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
